import axios, { AxiosInstance } from 'axios';

declare const process: { env: { REACT_APP_API_URL?: string } };

// Build-time env var injected by deploy.sh → REACT_APP_API_URL.
// Runtime safety net: if the page is served over HTTPS but the baked-in URL
// still uses http:// (e.g. stale build, wrong env var), browsers will block
// the request as mixed content.  Upgrade the scheme automatically so the app
// remains functional even when the deployment env var lags behind the TLS rollout.
const BASE_URL = (() => {
  const url = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return url.replace(/^http:\/\//, 'https://');
  }
  return url;
})();
export const STORAGE_BASE_URL = BASE_URL.replace(/\/api\/v1$/, '');

/** Resolves a possibly-relative storage path returned by the API to an absolute URL. */
export function resolveStorageUrl(path?: string | null): string | null {
  if (!path) return null;
  if (/^https?:\/\//.test(path)) return path;
  return `${STORAGE_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
}

export interface User { id: string; email: string; full_name: string; role: string; is_active: boolean; tenant_id?: string | null; }
export type CVModelProvider = 'local_yolo26' | 'cloud_yolo26' | 'roboflow_nas';
export type SubstrateModelProvider = 'heuristic' | 'anthropic_claude' | 'openai_gpt4o';
export interface Tenant { id: string; name: string; created_by?: string; is_active: boolean; created_at: string; cv_model_provider: CVModelProvider; substrate_model_provider: SubstrateModelProvider; }
export interface TenantMember { id: string; tenant_id: string; user_id: string; role: string; joined_at: string; user: User; }
export interface TenantInvite { id: string; tenant_id: string; email: string; role: string; method: string; token: string; status: string; invited_by?: string; message?: string; created_at: string; }
export interface Project { id: string; name: string; description?: string; address?: string; client_name?: string; created_at: string; }
export interface Inspection {
  id: string; project_id: string; inspector_id: string; title: string; status: string;
  location_description?: string; notes?: string; defect_count: number; created_at: string; completed_at?: string;
}
export interface BoundingBox { x: number; y: number; width: number; height: number; }
export interface DetectionResult {
  defect_type: string;
  severity: string;
  confidence: number;
  bounding_box: BoundingBox;
  description: string;
  segmentation_mask?: number[][]; // Polygon coordinates [[x1,y1], [x2,y2], ...] from YOLO26 instance segmentation
  detector_version?: string;
}
export interface AnalyzeImageResponse {
  image_id: string;
  inspection_id: string;
  detections: DetectionResult[];
  defects_created: number;
  annotated_image_url?: string | null;
}
/**
 * A frame analyzed for an inspection. Listed separately from defects because a
 * clean frame produces no Defect rows — a report built only from /defects has no
 * way to know the image exists.
 */
export interface InspectionImage {
  id: string;
  inspection_id: string;
  s3_key: string;
  image_url?: string | null;
  filename?: string | null;
  captured_at: string;
  device_type?: string | null;
  defect_count: number;
}
export interface Defect {
  id: string; inspection_id: string; image_id?: string; defect_type: string; severity: string;
  confidence?: number; description?: string; bounding_box?: BoundingBox;
  segmentation_mask?: { polygon?: number[][] }; // Stored as JSON in DB: {polygon: [[x1,y1], [x2,y2], ...]}
  is_confirmed: boolean; annotated_image_key?: string; annotated_image_url?: string; created_at: string;
  detector_version?: string;
}

// ── Property Scans (repaint-decision workflow) ────────────────────────────────
export type SurfaceCondition = 'excellent' | 'good' | 'fair' | 'poor';
export type ExposureLevel = 'low' | 'moderate' | 'high_uv';

export interface MaterialEstimate {
  primer_gallons: number; topcoat_gallons: number; recommended_coats: number; waste_factor_pct: number;
}
export interface CostPrediction { low: number; average: number; high: number; }
export interface RecommendedProduct {
  name: string; manufacturer: string; coverage_sqft_per_gal: number; lifespan_years: number; description: string;
}
export interface PropertyScan {
  id: string; project_id?: string | null; property_name: string; image_url?: string | null;
  surface_type?: string | null; existing_coating?: string | null; current_condition?: SurfaceCondition | null;
  color_sheen?: string | null; coats_detected?: number | null; paint_thickness_mils?: number | null;
  exposure?: ExposureLevel | null; square_footage?: number | null; location?: string | null;
  condition_score?: number | null;
  material_estimate?: MaterialEstimate | null;
  cost_prediction?: CostPrediction | null;
  recommended_products?: RecommendedProduct[] | null;
  ai_visual_assessment?: string | null;
  created_at: string; updated_at: string;
}
export interface PaintProduct {
  id: string; name: string; manufacturer: string; rating?: number | null; lifespan_years?: number | null;
  recoat_years?: number | null; coverage_sqft_per_gal?: number | null; cost_per_gal?: number | null;
  surface_tags?: string[] | null; description?: string | null;
}

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({ baseURL: BASE_URL });
    this.client.interceptors.request.use(config => {
      const token = localStorage.getItem('auth_token');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
    this.client.interceptors.response.use(
      res => res,
      err => {
        if (err.response?.status === 401) {
          const requestUrl = err.config?.url || '';
          if (!requestUrl.includes('/auth/login')) {
            localStorage.removeItem('auth_token');
            window.location.href = '/login';
          }
        }
        return Promise.reject(err);
      }
    );
  }

  // Auth
  async login(email: string, password: string) {
    const form = new FormData();
    form.append('username', email); form.append('password', password);
    const res = await this.client.post('/auth/login', form);
    localStorage.setItem('auth_token', res.data.access_token);
    return res.data as { access_token: string; user: User };
  }
  async loginWithPin(email: string, pin: string) {
    const res = await this.client.post('/auth/login/pin', { email, pin });
    localStorage.setItem('auth_token', res.data.access_token);
    return res.data as { access_token: string; user: User };
  }
  async setPin(pin: string): Promise<void> { await this.client.post('/auth/pin', { pin }); }
  async clearPin(): Promise<void> { await this.client.delete('/auth/pin'); }
  async register(
    email: string,
    password: string,
    fullName: string,
    role: string = 'inspector',
    companyName?: string,
    invitationToken?: string,
  ) {
    const res = await this.client.post('/auth/register', {
      email,
      password,
      full_name: fullName,
      role,
      company_name: companyName,
      invitation_token: invitationToken,
    });
    return res.data as User;
  }
  logout() { localStorage.removeItem('auth_token'); }

  // Users
  async getMe(): Promise<User> { return (await this.client.get('/users/me')).data; }
  async getUsers(): Promise<User[]> { return (await this.client.get('/users/')).data; }

  // Projects
  async getProjects(): Promise<Project[]> { return (await this.client.get('/projects/')).data; }
  async createProject(data: Partial<Project>): Promise<Project> { return (await this.client.post('/projects/', data)).data; }
  async updateProject(id: string, data: Partial<Project>): Promise<Project> { return (await this.client.patch(`/projects/${id}`, data)).data; }

  // Inspections
  async getInspections(projectId?: string): Promise<Inspection[]> {
    return (await this.client.get('/inspections/', { params: projectId ? { project_id: projectId } : {} })).data;
  }
  async getInspection(id: string): Promise<Inspection> { return (await this.client.get(`/inspections/${id}`)).data; }
  /** Every analyzed frame, oldest first — including ones with zero defects. */
  async getInspectionImages(id: string): Promise<InspectionImage[]> {
    return (await this.client.get(`/inspections/${id}/images`)).data;
  }
  /**
   * Mints a fresh download URL for one stored object, at the moment it is needed.
   *
   * URLs embedded in list responses go stale: they carry a fixed expiry and, on
   * ECS, are signed with the task's temporary role credentials, so they stop
   * working when the task is replaced. Fetching at click time keeps them seconds
   * old and signed by a live task.
   */
  async getImageUrl(inspectionId: string, key: string): Promise<string> {
    const res = await this.client.get(`/inspections/${inspectionId}/image-url`, { params: { key } });
    return res.data.url as string;
  }
  async createInspection(data: object): Promise<Inspection> { return (await this.client.post('/inspections/', data)).data; }
  async updateInspection(id: string, data: object): Promise<Inspection> { return (await this.client.patch(`/inspections/${id}`, data)).data; }
  async analyzeImage(inspectionId: string, file: File): Promise<AnalyzeImageResponse> {
    const form = new FormData();
    form.append('file', file);
    form.append('device_type', 'web');
    return (await this.client.post(`/inspections/${inspectionId}/analyze`, form)).data;
  }

  // Defects
  async getDefects(inspectionId: string): Promise<Defect[]> {
    return (await this.client.get(`/defects/inspection/${inspectionId}`)).data;
  }
  async confirmDefect(defectId: string, confirmed: boolean, notes?: string): Promise<Defect> {
    return (await this.client.patch(`/defects/${defectId}/confirm`, { is_confirmed: confirmed, notes })).data;
  }
  async deleteDefect(defectId: string): Promise<void> { await this.client.delete(`/defects/${defectId}`); }

  // Property Scans (repaint-decision workflow)
  async createPropertyScan(data: Partial<PropertyScan> & { property_name?: string }): Promise<PropertyScan> {
    return (await this.client.post('/property-scans/', data)).data;
  }
  async getPropertyScans(projectId?: string): Promise<PropertyScan[]> {
    return (await this.client.get('/property-scans/', { params: projectId ? { project_id: projectId } : {} })).data;
  }
  async getPropertyScan(id: string): Promise<PropertyScan> { return (await this.client.get(`/property-scans/${id}`)).data; }
  async updatePropertyScan(id: string, data: Partial<PropertyScan>): Promise<PropertyScan> {
    return (await this.client.patch(`/property-scans/${id}`, data)).data;
  }
  async analyzePropertyScan(id: string, file: File, squareFootage?: number): Promise<PropertyScan> {
    const form = new FormData();
    form.append('file', file);
    if (squareFootage != null) form.append('square_footage', String(squareFootage));
    return (await this.client.post(`/property-scans/${id}/analyze`, form)).data;
  }

  // Paint Products (reference catalog)
  async getPaintProducts(): Promise<PaintProduct[]> { return (await this.client.get('/paint-products/')).data; }

  // Reports
  async generateReport(inspectionId: string, format: 'pdf' | 'csv' = 'pdf'): Promise<string> {
    const res = await this.client.post('/reports/generate', {
      inspection_id: inspectionId, format, include_images: true,
    });
    return res.data.report_url;
  }

  // Tenant Management
  async getMyTenant(): Promise<Tenant> { return (await this.client.get('/tenants/me')).data; }
  async updateTenantSettings(tenantId: string, cvModelProvider: CVModelProvider, substrateModelProvider: SubstrateModelProvider): Promise<Tenant> {
    return (await this.client.patch(`/tenants/${tenantId}/settings`, {
      cv_model_provider: cvModelProvider,
      substrate_model_provider: substrateModelProvider,
    })).data;
  }
  async getTenantMembers(tenantId: string): Promise<TenantMember[]> {
    return (await this.client.get(`/tenants/${tenantId}/members`)).data;
  }
  async inviteTenantMember(tenantId: string, email: string, role: string, method: string = 'email', message?: string): Promise<TenantInvite> {
    return (await this.client.post(`/tenants/${tenantId}/invite`, { email, role, method, message })).data;
  }
  async updateTenantMember(tenantId: string, userId: string, role?: string, isActive?: boolean): Promise<TenantMember> {
    return (await this.client.patch(`/tenants/${tenantId}/members/${userId}`, { role, is_active: isActive })).data;
  }
  async removeTenantMember(tenantId: string, userId: string): Promise<void> { await this.client.delete(`/tenants/${tenantId}/members/${userId}`); }
  getDownloadUrl(inspectionId: string, format: 'pdf' | 'csv'): string {
    return `${BASE_URL}/reports/download/${inspectionId}?format=${format}&access_token=${localStorage.getItem('auth_token')}`;
  }
}

export const api = new ApiService();
